export const KINFEED_PAGE_SIZE = 20;

export type KinfeedFeedMode = 'local' | 'latest' | 'saved';

export type FeedPreferencePost = {
  id: string;
  user_id: string;
};

export const filterKinfeedPostsForViewer = <T extends FeedPreferencePost>(
  posts: T[],
  hiddenPostIds: Iterable<string>,
  mutedMemberIds: Iterable<string>,
) => {
  const hidden = hiddenPostIds instanceof Set ? hiddenPostIds : new Set(hiddenPostIds);
  const muted = mutedMemberIds instanceof Set ? mutedMemberIds : new Set(mutedMemberIds);

  return posts.filter((post) => !hidden.has(post.id) && !muted.has(post.user_id));
};

export type ThreadedFeedComment = {
  id: string;
  parent_id?: string | null;
  created_at?: string | null;
};

export type FeedCommentThread<T extends ThreadedFeedComment> = {
  comment: T;
  replies: T[];
};

const compareCommentTime = <T extends ThreadedFeedComment>(left: T, right: T) => {
  const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
  const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
  return leftTime - rightTime || left.id.localeCompare(right.id);
};

export const buildFeedCommentThreads = <T extends ThreadedFeedComment>(
  comments: T[],
): FeedCommentThread<T>[] => {
  const roots = comments.filter((comment) => !comment.parent_id).sort(compareCommentTime);
  const rootIds = new Set(roots.map((comment) => comment.id));
  const repliesByParent = new Map<string, T[]>();

  for (const comment of comments) {
    if (!comment.parent_id || !rootIds.has(comment.parent_id)) continue;
    const replies = repliesByParent.get(comment.parent_id) ?? [];
    replies.push(comment);
    repliesByParent.set(comment.parent_id, replies);
  }

  return roots.map((comment) => ({
    comment,
    replies: (repliesByParent.get(comment.id) ?? []).sort(compareCommentTime),
  }));
};

export const combineKinfeedPages = <T extends { id: string }>(current: T[], next: T[]) => {
  const byId = new Map(current.map((item) => [item.id, item]));
  for (const item of next) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
};
